// 1. The AlgoliaSearch component is a React functional component that performs product searches using Algolia.
// 2. It retrieves the search query from the URL using useSearchParams and fetches matching product results from an Algolia index.
// 3. The results are displayed as a grid of product tiles, each rendered with the Tile component.
// 4. If no results are found, it shows a message indicating this.
// 5. The component uses useEffect to trigger the search whenever the query changes.
//For testing
import React, { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { searchClient } from 'algolia/searchClient';
import { useFormat } from 'helpers/hooks/useFormat';
import Tile from './Tile';
import { Product } from './Hit';
import NotFound from './NotFound';
import FacetFilters from './Facets';
import Link from 'next/link';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import Breadcrumb from 'public/locales/en/product.json';
import Select from 'components/commercetools-ui/atoms/dropdown/option-dropdown';
import selectOptions from 'public/locales/en/product.json';
import { Option } from 'components/commercetools-ui/atoms/dropdown';
import Modal from 'components/commercetools-ui/atoms/modal';
import Prices from '../../../product-tile/prices';
import { useRouter } from 'next/navigation';
import beforeCompare from 'public/locales/en/product.json';
import Loading from 'components/commercetools-ui/atoms/loading';
import SortDropdown from './sortDropdown';
import { AdjustmentsHorizontalIcon as FiltersIcon } from '@heroicons/react/24/solid';
import Price from './price';
import useLocale from 'helpers/hooks/useLocale';
import { useAccount, useCart } from 'frontastic';
import { DataLayerHelpers, DataLayerOnLoad } from 'helpers/dataLayerHelpers';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import { StringHelpers } from 'helpers/stringHelpers';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';

type ProductHit = {
  standalonePrice?: { priceGB?: number }[];
};

const AlgoliaSearch = (badgeData: any) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Product[]>([]);
  const [facet, setFacet] = useState({});
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({
    brands: [],
    categories: [],
    weight: [],
    priceRange: [],
  });

  const [open, setOpen] = useState(false);
  const [compareProducts, setCompareProducts] = useState<any>(() => {
    const storedProducts = sessionStorage.getItem('compareProduct');
    if (typeof window !== 'undefined') {
      const storedProducts = sessionStorage?.getItem('compareProduct');
      if (storedProducts) {
        try {
          return JSON.parse(storedProducts);
        } catch (e) {
          console.error('Error parsing compareProduct from sessionStorage:', e);
          return [];
        }
      }
    }
    return [];
  });
  const [selectedOption, setSelectedOption] = useState<Option>();
  const [index, setIndex] = useState<any>();
  // const searchParams = new URLSearchParams(window.location.search);
  const searchParams = useSearchParams();
  const query: any = searchParams.get('q')?.split('?')[0];
  const router = useRouter();
  const { ctLocale: locale, getCountry, getCurrency } = useLocale();
  // const mainIndex =
  //   locale === 'en' || locale === 'en_GB'
  //     ? process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US
  //     : process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE;
  let mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US;
  // const hostName = typeof window !== 'undefined' ? window.location.hostname : '';
  // if (hostName?.includes('.eu') || hostName?.includes('-eu')) {
  if (getCurrency() !== 'GBP') {
    mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE;
  } else if (locale === 'en' || locale === 'en_GB') {
    mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_US;
  } else {
    mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_DE;
  }
  const lowIndex = `${mainIndex}_price_asc`;
  const highIndex = `${mainIndex}_price_desc`;
  const [lowPrice, setLowPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(10);
  const [priceRange, setPriceRange] = useState([lowPrice, maxPrice]); // Fallback range
  const [isPriceRangeReady, setIsPriceRangeReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [queryId, setQueryId] = useState<string>('c88b5922eb290afff34eea4842504852');
  const { loggedIn, result } = useAccount();

  const pathname = usePathname(); // Get the current route
  // Get current URL params

  const [limit, setLimit] = useState(24); // Start with 24 results
  const [totalHits, setTotalHits] = useState(0); // Track total available results
  const [hasMore, setHasMore] = useState(true); // Control Load More button
  const [trustPilotSkuId, setTrustPilotSkuId] = useState('');

  useEffect(() => {
    if (!filters) return;

    const newSearchParams = new URLSearchParams(searchParams.toString()); // Preserve existing params

    let index = 1;
    Object.entries(filters).forEach(([key, values]) => {
      if (key === 'priceRange') return;
      if (values.length > 0) {
        // Combine multiple values using "|" (pipe separator)
        const combinedValues = values.join('%7C');

        newSearchParams.set(`prefn${index}`, key);
        newSearchParams.set(`prefv${index}`, combinedValues);

        index++;
      }
    });

    // Adding Price Range if it's selected
    // if (priceRange[0] > 0 || priceRange[1] > 0) {
    //   newSearchParams.set("pmin", priceRange[0].toString());
    //   newSearchParams.set("pmax", priceRange[1].toString());
    // }

    // Update the URL dynamically
    router.push(`${pathname}?${newSearchParams.toString()}`);
  }, [filters]);

  useEffect(() => {
    if (lowPrice > 0 && maxPrice > 0) {
      setIsPriceRangeReady(true);
    }
  }, [lowPrice, maxPrice]);

  useEffect(() => {
    if (lowPrice > 0 && maxPrice > 0) {
      setPriceRange([lowPrice, maxPrice]);
    }
  }, [lowPrice, maxPrice]);

  useEffect(() => {
    const indexName: any = selectedOption?.value ? selectedOption?.value : mainIndex;
    if (indexName) {
      const newIndex = searchClient.initIndex(indexName);
      setIndex(newIndex);
    }
  }, [selectedOption, setIndex]);

  useEffect(() => {
    if (!searchClient) return;

    const fetchPriceLimits = async () => {
      try {
        const filterString = Object.entries(filters)
          .map(([facetType, values]) => values.map((value) => `${facetType}:"${value}"`).join(' OR '))
          .filter(Boolean)
          .join(' AND ');

        // Initialize indices
        const lowPriceIndex = searchClient.initIndex(lowIndex);
        const highPriceIndex = searchClient.initIndex(highIndex);
        // Fetch lowest price
        const lowPriceResult = await lowPriceIndex.search<ProductHit>(query || '', {
          facets: ['*'],
          filters: filterString,
          hitsPerPage: 10,
        });

        const highPriceResult = await highPriceIndex.search<ProductHit>(query || '', {
          facets: ['*'],
          filters: filterString,
          hitsPerPage: 10,
        });

        const minPriceHit = lowPriceResult.hits.find((hit: any) => hit?.standalonePrice?.[0]?.priceGB > 0);
        const minPrice = minPriceHit?.standalonePrice?.[0]?.priceGB || 0;

        const maxPriceHit = highPriceResult.hits.find((hit: any) => hit?.standalonePrice?.[0]?.priceGB > 0);
        const maxPrice = maxPriceHit?.standalonePrice?.[0]?.priceGB || 0;

        if (minPrice !== 0 && maxPrice !== 0) {
          setLowPrice(minPrice);
          setMaxPrice(maxPrice);
          setPriceRange([minPrice, maxPrice]);
        } else {
          console.error('Invalid price range:', { minPrice, maxPrice });
        }
      } catch (error) {
        console.error('Error fetching price range:', error);
      }
    };

    fetchPriceLimits();
  }, [searchClient, query, filters]);

  useEffect(() => {
    if (!index) return;
    // Construct filter string for other facets
    let filterString = Object.entries(filters)
      .filter(([facetType]) => facetType !== 'priceRange')
      .map(([facetType, values]) =>
        values.length ? values.map((value) => `${facetType}:"${value}"`).join(' OR ') : '',
      )
      .filter(Boolean)
      .join(' AND ');

    if (filters.priceRange && filters.priceRange.length === 2) {
      const [minPrice, maxPrice] = filters.priceRange;
      const priceRangeFilter = `standalonePrice.priceGB >= ${minPrice} AND standalonePrice.priceGB <= ${maxPrice}`;

      if (filterString) {
        filterString += ` AND ${priceRangeFilter}`;
      } else {
        filterString += priceRangeFilter;
      }
    }

    index
      .search(query, {
        facets: ['*'],
        filters: filterString,
        hitsPerPage: limit,
        clickAnalytics: true,
      })
      .then(
        ({
          hits,
          facets,
          nbHits,
          queryID,
        }: {
          hits: any;
          facets?: Record<string, Record<string, number>>;
          nbHits: any;
          queryID: string;
        }) => {
          setResults(hits);
          setTotalHits(nbHits);
          setHasMore(limit < nbHits);
          setQueryId(queryID);

          if (facets) {
            setFacet((prevFacet: any) => {
              const updatedFacet = { ...prevFacet };
              Object.keys(facets).forEach((facetType) => {
                updatedFacet[facetType] = {
                  ...prevFacet[facetType],
                  ...facets[facetType],
                };
              });
              return updatedFacet;
            });
          }
        },
      )
      .catch((err: any) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [query, filters, index, limit]);
  useEffect(() => {
    setLoading(true);
  }, [query]);

  useEffect(() => {
    if (compareProducts.length) {
      sessionStorage.setItem('compareProduct', JSON.stringify(compareProducts));
    }
  }, [compareProducts]);

  const handleCompareProduct = (product: Product) => {
    setCompareProducts((prevProducts: any) => {
      const products = prevProducts?.[1] || [];

      const isProductInList = products?.some((p: any) => p?.key === product?.key);
      const isQueryInList = prevProducts[0]?.query === 'shoes';

      if (products?.length >= 4 && !isProductInList) {
        alert('You can only compare up to 4 products.');
        return prevProducts;
      }

      if (isProductInList) {
        const updatedProducts = products?.filter((p: any) => p?.key !== product?.key);
        return [{ query: query }, updatedProducts];
      } else {
        const updatedProducts = [...products, product];
        return [{ query: query }, updatedProducts];
      }
    });

    const skus = product.variants
      .filter((v) => v.sku)
      .map((v) => StringHelpers.removeWhiteSpace(v.sku))
      .join(',');
    if (skus) {
      setTrustPilotSkuId(skus);
    }
  };

  useEffect(() => {
    const storedQuery = sessionStorage.getItem('query');

    if (query && storedQuery !== query) {
      sessionStorage.removeItem('compareProduct');
      setCompareProducts([{ query: query }, []]);
      sessionStorage.setItem('query', query);
    }
  }, [query]);

  const productsToCompare = compareProducts?.[1];
  // const pathname = usePathname();

  const comparePage = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('previousUrl', pathname);
    }
    router.push(`/compare`);
  };

  const removeProductFromSession = (productKey: string) => {
    setCompareProducts((prevProducts: any) => {
      const products = prevProducts?.[1] || [];

      const updatedProducts = products.filter((product: any) => product?.key !== productKey);

      const updatedCompareProducts = [{ query: prevProducts[0]?.query }, updatedProducts];
      sessionStorage.setItem('compareProduct', JSON.stringify(updatedCompareProducts));

      return updatedCompareProducts;
    });
  };
  const { account } = useAccount();

  useEffect(() => {
    const localeData = {
      country: getCountry(),
      currency: getCurrency(),
    };
    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerOnLoad(currentUrl, 'Search-PLP', localeData, account);
    dataLayer.setDataLayerOnLoad();
  }, []);

  if (/^[^\w\s]+$/.test(query)) {
    return <NotFound />;
  }

  const userId = loggedIn
    ? result?.data?.data?.account?.accountId
    : typeof window !== 'undefined' && localStorage.getItem('__anon_id');

  const handleProductClickAfterSearch = (locale: string, objectId: string[], position: number[]) => {
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleClickAfterSearch(objectId, userId, queryId, position);
  };

  const handleProductSwatchClickAfterSearch = (locale: string, objectId: string[], index: number) => {
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleColourSwatchesClickedAfterSearch(objectId, userId, queryId, index);
  };
  const handleClickLoadMore = (locale: string) => {
    const objectIDs = results.map((item) => item?.objectID);
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleClickLoadMore(objectIDs, userId);
  };
  const loadMore = () => {
    handleClickLoadMore(locale);
    setLimit((prevLimit) => prevLimit + 24);
  };

  const handleClickSortIndex = (locale: string, eventName: string) => {
    const objectIDs = results.map((item) => item?.objectID);
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleSortClicked(objectIDs, userId, eventName);
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : results?.length === 0 ? (
        <NotFound query={query} />
      ) : (
        <div className="flex max-w-[1200px] flex-col justify-center md:p-30 lg:mx-auto xl:p-0">
          <div className="hidden lg:grid lg:grid-cols-12">
            <div className="col-span-12 mb-32 mt-20 flex items-center justify-around">
              <div className="flex items-center pl-15">
                <Link href={'/'} className="text-14">
                  {Breadcrumb?.BreadCrumb}
                </Link>
                <ChevronRightIcon
                  className="mt-2 h-15 w-18 font-bold text-[#127e57]"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                />
                <p className="text-14 capitalize">
                  <span> {`${query?.toLowerCase()}`}</span>
                </p>
              </div>

              <h1 className="mt-5 flex-1 text-center text-36 font-semibold">Search results - {query?.toUpperCase()}</h1>

              <div className="flex items-center gap-x-5 text-14">
                <p className="min-w-[50px] font-body text-14 font-normal leading-7">Sort by:</p>
                <SortDropdown
                  setSelectedOption={setSelectedOption}
                  selectButtonClassName="min-w-100"
                  options={selectOptions?.sortOptions}
                  defaultValue={selectedOption}
                  mainIndex={mainIndex}
                  handleClickSortIndex={handleClickSortIndex}
                />
                {/* <Select
                  setSelectedOption={setSelectedOption}
                  selectButtonClassName="w-100"
                  options={selectOptions?.sortOptions}
                  defaultValue={selectedOption}
                /> */}
              </div>
            </div>
          </div>
          <div className="mb-25 mt-15 flex items-center px-16 pl-15 lg:hidden">
            <Link href={'/'} className="text-14">
              {Breadcrumb?.BreadCrumb}
            </Link>
            <ChevronRightIcon
              className="mt-2 h-15 w-18 font-bold text-[#127e57]"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
            />
            <p className="text-14">
              <span> {`${query}`}</span>
            </p>
          </div>
          <div className="mb-25 flex items-center justify-center lg:hidden">
            <span className="font-body text-29 font-semibold leading-[29px]">Search results -</span>
            <span className="ml-5 mt-3">{query?.toUpperCase()}</span>
          </div>
          <div className="mobile-section flex items-center justify-between px-15 pb-16 lg:hidden">
            <button
              className="flex min-w-[80px] cursor-pointer items-center gap-8 rounded-md border border-transparent bg-white px-12 py-6 text-14 leading-[20px] transition hover:border-gray-500"
              onClick={() => setIsOpen(!isOpen)}
            >
              <FiltersIcon className="mt-2 w-16 stroke-green-600" />
              <span className="text-14">{'Filters'}</span>
            </button>

            <SortDropdown
              setSelectedOption={setSelectedOption}
              selectButtonClassName=""
              options={selectOptions?.sortOptions}
              defaultValue={selectedOption}
              mainIndex={mainIndex}
              handleClickSortIndex={handleClickSortIndex}
            />
          </div>

          <div className="grid grid-cols-1 gap-2 lg:mx-auto lg:grid-cols-[15%_85%] lg:gap-4">
            {results.length > 0 && (
              <div
                className={`section-1 pl-20 pr-30 text-sm font-normal lg:block lg:px-0 ${isOpen ? 'block' : 'hidden'}`}
              >
                <FacetFilters
                  facet={facet}
                  onFiltersChange={setFilters}
                  lowPrice={lowPrice}
                  maxPrice={maxPrice}
                  priceRange={priceRange}
                  setPriceRange={setPriceRange}
                  isPriceRangeReady={isPriceRangeReady}
                  isOpen={isOpen}
                />
              </div>
            )}
            <div className="section-2">
              <div className="border-3 grid grid-cols-2 border border-[#f5f4f3] md:grid-cols-3 md:border-none lg:grid-cols-3">
                {results?.map((i: Product, index: number) => {
                  return (
                    <Tile
                      product={i}
                      key={index}
                      tileKey={index}
                      // storeProductToSession={handleCompareProduct}
                      compareProducts={compareProducts}
                      setOpen={setOpen}
                      badgeData={badgeData}
                      handleProductClickAfterSearch={handleProductClickAfterSearch}
                      handleProductSwatchClickAfterSearch={handleProductSwatchClickAfterSearch}
                    />
                  );
                })}
              </div>
              {hasMore && (
                <div className="my-4 flex justify-center">
                  <button
                    onClick={loadMore}
                    className="mx-auto mt-[90px] block rounded-md bg-primary-black px-48 py-12 text-16 font-medium text-white transition hover:bg-gray-500 disabled:bg-neutral-400 disabled:opacity-0"
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {productsToCompare?.length !== 0 && (
        <Modal isOpen={open} style={{ content: { maxWidth: '1200px', height: '572px', bottom: 0, right: 0 } }}>
          <div className="flex items-center justify-end">
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 48 48">
              <path fill="#F44336" d="M21.5 4.5H26.501V43.5H21.5z" transform="rotate(45.001 24 24)"></path>
              <path fill="#F44336" d="M21.5 4.5H26.5V43.501H21.5z" transform="rotate(135.008 24 24)"></path>
            </svg>
            <input type="button" onClick={() => setOpen(false)} value={'Close'} />
          </div>
          <div>
            <div className="m-15 flex w-full justify-evenly">
              <div className={`flex h-[493px] w-[240px] flex-col items-center  border-slate-700`}>
                <img src={productsToCompare?.[0]?.variants?.[0]?.images?.[0]} className="" />
                <hr className="my-2 w-full border-t border-slate-500" />

                <p className="mb-20 mt-10 h-59  w-full text-center text-22 font-bold leading-[22px]">
                  {productsToCompare?.[0]?.name?.['en-GB']}
                </p>

                <div className="mb-10 py-7">
                  <TrustBox id={trustPilotSkuId} showVertical={false} />
                </div>

                <div className="mt-20">
                  <Price
                    prices={productsToCompare?.[0]?.standalonePrice}
                    discountedPrice={productsToCompare?.[0]?.variants?.[0]?.discountedPrice}
                  />
                </div>
                <hr className="my-2 mt-20 w-full border-t border-slate-500" />
                <div className="mt-20 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 48 48">
                    <path fill="#F44336" d="M21.5 4.5H26.501V43.5H21.5z" transform="rotate(45.001 24 24)"></path>
                    <path fill="#F44336" d="M21.5 4.5H26.5V43.501H21.5z" transform="rotate(135.008 24 24)"></path>
                  </svg>
                  <input
                    type="button"
                    onClick={() => removeProductFromSession(productsToCompare?.[0]?.key)}
                    value={'Remove'}
                    className="cursor-pointer"
                  />
                </div>
              </div>

              {beforeCompare.beforeCompare?.map((item, index) => {
                const product = productsToCompare?.[index + 1];

                return (
                  <div key={index} className="flex h-[493px] w-[240px] flex-col items-center justify-center ">
                    {product ? (
                      <>
                        <div className={`flex h-[493px] w-[240px] flex-col items-center  border-slate-700`}>
                          <img src={product?.variants?.[0]?.images?.[0]} className="" />
                          <hr className="my-2 w-full border-t border-slate-500" />
                          <p className="mb-20 mt-10 h-59 w-full text-center text-22 font-bold leading-[22px] ">
                            {product?.name?.['en-GB']}
                          </p>
                          <div className="mb-10 py-7">
                            <TrustBox id={trustPilotSkuId} showVertical={false} />
                          </div>
                          <div className="mt-20">
                            <Price
                              prices={product?.standalonePrice}
                              discountedPrice={product?.variants?.[0]?.discountedPrice}
                            />
                          </div>
                          <hr className="my-2 mt-20 w-full border-t border-slate-500" />
                          <div className="mt-20 flex  items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              x="0px"
                              y="0px"
                              width="15"
                              height="15"
                              viewBox="0 0 48 48"
                            >
                              <path
                                fill="#F44336"
                                d="M21.5 4.5H26.501V43.5H21.5z"
                                transform="rotate(45.001 24 24)"
                              ></path>
                              <path
                                fill="#F44336"
                                d="M21.5 4.5H26.5V43.501H21.5z"
                                transform="rotate(135.008 24 24)"
                              ></path>
                            </svg>
                            <input
                              onClick={() => removeProductFromSession(product?.key)}
                              className="cursor-pointer"
                              type="button"
                              value={'Remove'}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div
                        key={index}
                        className="flex h-[493px] w-[240px] flex-col items-center justify-center border-2 border-dashed border-slate-700"
                      >
                        <div className="text-42 font-semibold">{item?.number}</div>
                        <p className="text-center text-16 font-normal">
                          <span
                            className="mr-2 cursor-pointer underline underline-offset-4"
                            onClick={() => setOpen(false)}
                          >
                            Select
                          </span>
                          {item?.text}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex  justify-end ">
              <input
                type="button"
                className="h-[39px] w-[130px] cursor-pointer bg-[#1DCE8F] text-14"
                onClick={comparePage}
                value={'COMPARE NOW'}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AlgoliaSearch;
